// Marketing Practice Detail page — full intro for a single practice.
// Route: /practices/:slug. Visitor-facing (no auth).

import { useNavigate, useParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { AppMenu } from "../components/AppMenu";
import { useAuth } from "../App";
import { useBilling } from "../contexts/BillingContext";
import { ArrowLeft, ArrowRight, Check, Info, Lock } from "lucide-react";
import { PRACTICE_CATALOG, getPracticeBySlug } from "../lib/practiceCatalog";
import { getPracticeIntro, PRACTICE_TIME_NOTE } from "../lib/practiceIntros";
import { isLockedForUser } from "../lib/trialAccess";
import useSeo from "../hooks/useSeo";

const PracticeDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Billing status comes from the shared BillingContext (one fetch at
  // sign-in) so this detail page doesn't add another /api/billing/status
  // request on top of every marketing visit.
  const { billing } = useBilling();
  const billingStatus = billing?.status || null;

  const practice = getPracticeBySlug(slug);
  useSeo({
    title: practice ? `${practice.title} — Bilateral Calm` : "Practice — Bilateral Calm",
    description: practice ? practice.hook : undefined,
    path: `/practices/${slug || ""}`,
  });

  if (!practice) {
    return <Navigate to="/practices" replace />;
  }

  const intro = getPracticeIntro(practice.slug);
  const related = PRACTICE_CATALOG
    .filter((p) => p.group === practice.group && p.slug !== practice.slug)
    .slice(0, 4);

  const isLocked = user && isLockedForUser(practice.slug, billingStatus);
  const primaryCta = !user
    ? {
        label: "Sign up to try it",
        onClick: () => navigate("/pricing"),
        testid: "practice-detail-signup-btn",
      }
    : isLocked
    ? {
        label: "Upgrade to unlock",
        onClick: () => navigate("/pricing"),
        testid: "practice-detail-upgrade-btn",
      }
    : {
        label: `Start ${practice.title}`,
        onClick: () => navigate(`/intervention/${practice.slug}`),
        testid: "practice-detail-start-btn",
      };

  return (
    <div className="min-h-screen relative" data-testid="practice-detail-page">
      <div
        className="fixed inset-0 -z-10 bg-cover bg-bottom"
        style={{
          backgroundImage: "url('/images/learn-more-wallpaper.webp')",
          filter: "saturate(1.18) brightness(1.06)",
        }}
        aria-hidden
      />
      <div
        className="fixed inset-0 -z-10"
        style={{ backgroundColor: "rgba(251,248,241,0.88)" }}
        aria-hidden
      />

      {/* Header */}
      <header className="bg-[#FBF8F1]/80 backdrop-blur-md border-b border-slate-100/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center"
            data-testid="practice-detail-home-btn"
          >
            <img src="/logo-bilateral-calm.png" alt="Bilateral Calm" className="h-28 sm:h-32 w-auto" />
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            {!user && (
              <>
                <button
                  onClick={() => navigate("/practices")}
                  className="hidden sm:inline-flex text-sm text-[#4A5568] hover:text-[#1B2349] font-medium px-3 py-2 rounded-full"
                  data-testid="practice-detail-nav-practices-btn"
                >
                  Practice Library
                </button>
                <button
                  onClick={() => navigate("/pricing")}
                  className="hidden sm:inline-flex text-sm text-[#4A5568] hover:text-[#1B2349] font-medium px-3 py-2 rounded-full"
                  data-testid="practice-detail-nav-plans-btn"
                >
                  Plans
                </button>
                <Button data-testid="practice-detail-signin-btn" onClick={() => navigate("/sign-in")} className="btn-primary">
                  Sign in
                </Button>
              </>
            )}
            {user && <AppMenu />}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate("/practices")}
          className="text-sm text-[#4A5568] hover:text-[#1B2349] flex items-center gap-2 mb-6"
          data-testid="practice-detail-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> All practices in the library
        </button>

        {/* Hero */}
        <section className="grid md:grid-cols-2 gap-10 items-center mb-14">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium">
                {practice.group}
              </p>
              {isLocked && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E2B887]/15 text-[#7A5C2E] text-[11px] font-medium" data-testid="practice-detail-locked-chip">
                  <Lock className="w-3 h-3" />
                  Locked during trial
                </span>
              )}
            </div>
            <h1 className="font-outfit font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#1B2349] tracking-tight leading-[1.05] mb-4">
              {practice.title}
            </h1>
            <p className="text-lg text-[#1B2349]/80 leading-relaxed mb-6">{practice.hook}</p>
            <p className="text-base text-[#4A5568] leading-relaxed mb-4">{intro.body}</p>
            {/* Time expectation — same copy every practice; kept in
                sync via PRACTICE_TIME_NOTE in practiceIntros.js. */}
            <p
              className="text-sm text-[#4A5568] leading-relaxed mb-8 italic"
              data-testid="practice-detail-time-note"
            >
              {PRACTICE_TIME_NOTE}
            </p>
            {isLocked && (
              <p className="text-sm text-[#4A5568] mb-5 max-w-md" data-testid="practice-detail-locked-note">
                During your free trial you can run <strong>Quiet The Worry</strong> and <strong>Dial Down Body Tension</strong>. Upgrade to unlock every guided practice including this one.
              </p>
            )}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={primaryCta.onClick}
                className="btn-primary text-base px-6 py-3"
                data-testid={primaryCta.testid}
              >
                {primaryCta.label} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              {!user && (
                <Button
                  onClick={() => navigate("/sign-in")}
                  variant="outline"
                  className="rounded-full border-[#1B2349] text-[#1B2349] hover:bg-[#1B2349] hover:text-white"
                  data-testid="practice-detail-secondary-signin-btn"
                >
                  Already a member? Sign in
                </Button>
              )}
            </div>
          </div>
          <div className="order-first md:order-last">
            <div
              className="aspect-square w-full max-w-md mx-auto rounded-3xl bg-cover bg-center shadow-[0_10px_40px_rgba(27,35,73,0.18)] ring-1 ring-white/40"
              style={{ backgroundImage: `url('${practice.image}')` }}
              aria-label={`${practice.title} hero image`}
              role="img"
            />
          </div>
        </section>

        {/* Best for */}
        <section className="bg-white/65 backdrop-blur-md border border-white/40 rounded-3xl p-8 sm:p-10 mb-10 shadow-[0_4px_20px_rgba(27,35,73,0.06)]">
          <h2 className="font-outfit font-semibold text-xl sm:text-2xl text-[#1B2349] mb-5">
            This practice fits you if you're dealing with…
          </h2>
          <ul className="space-y-3">
            {practice.bestFor.map((point) => (
              <li key={point} className="flex items-start gap-3 text-[#1B2349] text-base leading-relaxed">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: practice.color }} />
                <span>{point}</span>
              </li>
            ))}
          </ul>

          {practice.medicalDisclaimer && (
            <div className="mt-6 flex items-start gap-3 bg-[#FFF7E6] border border-[#E2B887]/50 rounded-2xl p-4 text-sm text-[#7A5C2E]">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>{practice.medicalDisclaimer}</p>
            </div>
          )}
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mb-16">
            <h3 className="font-outfit font-semibold text-lg text-[#1B2349] mb-4">
              More in {practice.group}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {related.map((p) => (
                <button
                  key={p.slug}
                  onClick={() => navigate(`/practices/${p.slug}`)}
                  className="text-left rounded-2xl overflow-hidden bg-white/65 backdrop-blur-md border border-white/40 hover:bg-white/85 hover:-translate-y-1 transition-all duration-300 shadow-[0_4px_20px_rgba(27,35,73,0.06)]"
                  data-testid={`practice-related-${p.slug}`}
                >
                  <div
                    className="aspect-square w-full bg-cover bg-center"
                    style={{ backgroundImage: `url('${p.image}')` }}
                  />
                  <div className="p-3">
                    <p className="font-outfit font-medium text-sm text-[#1B2349] leading-tight">{p.title}</p>
                    <p className="text-[#4A5568] text-xs mt-1 leading-snug">{p.hook}</p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="bg-[#1B2349] text-white rounded-3xl p-10 text-center shadow-[0_10px_40px_rgba(27,35,73,0.25)]">
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mb-3">
            Ready to try {practice.title}?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-6">
            Get all 20 practices and the Daily Calming Audio library for 5 days, free. Cancel anytime.
          </p>
          <Button
            onClick={primaryCta.onClick}
            className="bg-white text-[#1B2349] hover:bg-[#FBF8F1] rounded-full text-base px-8 py-3 font-semibold"
            data-testid="practice-detail-bottom-cta"
          >
            {primaryCta.label}
          </Button>
        </section>
      </main>

      <footer className="py-8 px-6 border-t border-slate-100">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img src="/logo-bilateral-calm.png" alt="Bilateral Calm" className="h-20 sm:h-24 w-auto" />
          </div>
          <p className="text-[#718096] text-sm">
            © 2026 Mood and Sleep Clinic LLC · Bilateral Calm™ · <a href="mailto:mscwellness@protonmail.com" className="underline underline-offset-2 hover:text-[#1B2349]">mscwellness@protonmail.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PracticeDetailPage;
